import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as capabilitiesApi from "@/services/capabilitiesApi";
import type {
  CapabilityOverride,
  CapabilityOverrideBulkCreate,
  CapabilityOverrideState,
  CreateCapabilityOverrideRequest,
} from "@/services/capabilitiesApi";

export const capabilityKeys = {
  all: ["capabilities"] as const,
  overrides: (capability?: string) =>
    [...capabilityKeys.all, "overrides", capability ?? null] as const,
  registry: () => [...capabilityKeys.all, "registry"] as const,
  templates: () => [...capabilityKeys.all, "templates"] as const,
  explain: (capability: string, userId?: number, sourceId?: string) =>
    [
      ...capabilityKeys.all,
      "explain",
      capability,
      userId ?? null,
      sourceId ?? null,
    ] as const,
  orphans: () => [...capabilityKeys.all, "orphans"] as const,
  expired: () => [...capabilityKeys.all, "expired"] as const,
};

export function useCapabilityOverrides(capability?: string) {
  return useQuery({
    queryKey: capabilityKeys.overrides(capability),
    queryFn: () => capabilitiesApi.listCapabilityOverrides(capability),
  });
}

export function useCapabilityRegistry() {
  return useQuery({
    queryKey: capabilityKeys.registry(),
    queryFn: () => capabilitiesApi.fetchCapabilityRegistry(),
    staleTime: 5 * 60_000,
  });
}

export function useCapabilityTemplates() {
  return useQuery({
    queryKey: capabilityKeys.templates(),
    queryFn: () => capabilitiesApi.listCapabilityTemplates(),
    staleTime: 5 * 60_000,
  });
}

export function useCapabilityExplain(
  capability: string | undefined,
  params: { userId?: number | undefined; sourceId?: string | undefined } = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: capabilityKeys.explain(
      capability ?? "",
      params.userId,
      params.sourceId,
    ),
    queryFn: () =>
      capabilitiesApi.fetchCapabilityExplain(capability as string, params),
    enabled: (options.enabled ?? true) && Boolean(capability),
  });
}

export function useOrphanCapabilityOverrides() {
  return useQuery({
    queryKey: capabilityKeys.orphans(),
    queryFn: () => capabilitiesApi.listOrphanCapabilityOverrides(),
  });
}

export function useExpiredCapabilityOverrides() {
  return useQuery({
    queryKey: capabilityKeys.expired(),
    queryFn: () => capabilitiesApi.listExpiredCapabilityOverrides(),
  });
}

export function useCreateCapabilityOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCapabilityOverrideRequest) =>
      capabilitiesApi.createCapabilityOverride(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
    },
  });
}

export function useBulkCreateCapabilityOverrides() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CapabilityOverrideBulkCreate) =>
      capabilitiesApi.bulkCreateCapabilityOverrides(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
    },
  });
}

export function useDeleteCapabilityOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (overrideId: number) =>
      capabilitiesApi.deleteCapabilityOverride(overrideId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
    },
  });
}

export function usePurgeExpiredCapabilityOverrides() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => capabilitiesApi.purgeExpiredCapabilityOverrides(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
    },
  });
}

export interface BulkCapabilityDecisionResult {
  succeeded: number[];
  failed: { id: number; error: string }[];
}

export function useBulkCapabilityDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      overrides,
      decision,
      reason,
      expires_at,
    }: {
      overrides: CapabilityOverride[];
      decision: "inherit" | CapabilityOverrideState;
      reason?: string;
      expires_at?: string | null;
    }): Promise<BulkCapabilityDecisionResult> => {
      const succeeded: number[] = [];
      const failed: { id: number; error: string }[] = [];

      for (const override of overrides) {
        try {
          await capabilitiesApi.deleteCapabilityOverride(override.id);
          if (decision !== "inherit") {
            await capabilitiesApi.createCapabilityOverride({
              capability: override.capability,
              scope_type: override.scope_type,
              scope_id: override.scope_id,
              state: decision,
              reason: (reason ?? "").trim(),
              ...(expires_at ? { expires_at } : {}),
            });
          }
          succeeded.push(override.id);
        } catch (error) {
          failed.push({
            id: override.id,
            error: error instanceof Error ? error.message : "Failed",
          });
        }
      }

      return { succeeded, failed };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
    },
  });
}
